import React, { useState } from 'react';
import BrandLogo from '../components/BrandLogo';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/client/login`, { email, password });
      localStorage.setItem('client_token', data.token);
      localStorage.setItem('client_name', data.client.name);
      localStorage.setItem('client_email', data.client.email);
      toast.success('Login successful');
      navigate('/client/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 lg:p-16 bg-white">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-12">
            <BrandLogo className="h-12 mb-4" />
            <p className="text-sm text-zinc-500">Client Portal</p>
          </div>

          <h1 className="text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome Back
          </h1>
          <p className="text-zinc-600 mb-8">
            Sign in to track your jobs and download retouched files
          </p>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  data-testid="client-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  data-testid="client-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <Button
              data-testid="client-login-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {loading ? 'Signing in...' : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Your login credentials were sent to your email after job submission. 
              Login access is valid for 7 days.
            </p>
          </div>

          <div className="mt-6 text-center">
            <a 
              href="/"
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Submit a new test job
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel - Info */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-zinc-900 to-zinc-800 text-white p-16 items-center justify-center">
        <div className="max-w-md">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Your Retouched Files Await
          </h2>
          <p className="text-zinc-300 mb-8">
            Access your client portal to view job status, download retouched files, and provide feedback.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                <span className="text-sm">1</span>
              </div>
              <span className="text-zinc-300">View your job status in real-time</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                <span className="text-sm">2</span>
              </div>
              <span className="text-zinc-300">Download retouched files instantly</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                <span className="text-sm">3</span>
              </div>
              <span className="text-zinc-300">Approve or request revisions</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
